import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";

import { BookReaderQuery } from "arena/queries";
import useBookReader from "arena/hooks/useBookReader";
import useProductDialog from "arena/hooks/useProductDialog";
import FoliateReader from "./FoliateReader";
import ReaderControls from "./ReaderControls";
import BookReaderAccessErrors from "./BookReaderAccessErrors";
import ProductDialog from "arena/components/ProductDialog";
import ErrorScreen from "core/components/ErrorScreen";
import LoadingScreen from "core/components/LoadingScreen";
import { getUtmFromLocalStorage } from "core/utils/utm";
// import {
//   isTelegramMiniApp,
//   requestFullscreen,
//   exitFullscreen,
// } from "core/utils/telegram";

export default function BookReader() {
  const { contentId } = useParams();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation("arena");

  // Состояние компонента
  const [bookReaderError, setBookReaderError] = useState(null);
  const [uiVisible, setUiVisible] = useState(true);

  const currentPositionRef = useRef(0);
  const previousPositionRef = useRef(0);
  const initializeRefs = useRef(false);
  const readerControlsRef = useRef(null);

  const { startReading, recordProgress, finishReading, isReading, bookData } =
    useBookReader();

  const { state, dispatch } = useProductDialog();

  const { data, loading, error, refetch } = useQuery(BookReaderQuery, {
    variables: { id: contentId },
    fetchPolicy: "cache-and-network",
  });

  const content = data?.content;

  // Инициализация чтения при получении токена
  useEffect(() => {
    if (content?.access?.token && !isReading && !bookData) {
      const utm = getUtmFromLocalStorage();
      startReading(content.access.token, utm, content.getIpUrl).catch((err) => {
        setBookReaderError(err.message);
      });
    }
  }, [
    content?.access?.token,
    content?.getIpUrl,
    isReading,
    bookData,
    startReading,
  ]);

  // Инициализация позиции при получении bookData
  useEffect(() => {
    if (!initializeRefs.current && bookData) {
      currentPositionRef.current = 0;
      previousPositionRef.current = 0;
      initializeRefs.current = true;
    }
  }, [bookData]);

  // // Управление полноэкранным режимом в Telegram
  // useEffect(() => {
  //   if (isTelegramMiniApp) {
  //     requestFullscreen();
  //   }

  //   return () => {
  //     if (isTelegramMiniApp) {
  //       exitFullscreen();
  //     }
  //   };
  // }, []);

  // Обработка завершения чтения при размонтировании компонента
  useEffect(() => {
    return () => {
      if (isReading) {
        const finalCharactersRead = Math.max(
          0,
          currentPositionRef.current - previousPositionRef.current
        );
        finishReading(currentPositionRef.current, finalCharactersRead).catch(
          console.error
        );
      }
    };
  }, [isReading, finishReading]);

  // Обработчики событий (определяем ДО ранних возвратов)
  const handlePositionChange = useCallback(
    (characterPosition, location, fraction, cfi) => {
      const previousPosition = currentPositionRef.current;
      currentPositionRef.current = characterPosition;

      // Сохраняем только при значительном изменении позиции (перелистывание)
      const positionDiff = Math.abs(characterPosition - previousPosition);
      const isPageTurn = positionDiff > 100; // Если изменение больше 100 символов - это перелистывание

      if (isPageTurn && isReading) {
        const charactersRead = Math.max(
          0,
          characterPosition - previousPositionRef.current
        );

        // Немедленное сохранение при перелистывании
        recordProgress(characterPosition, charactersRead, fraction, cfi)
          .then(() => {
            previousPositionRef.current = characterPosition;
            return;
          })
          .catch((error) => {
            console.error("Failed to record progress on page turn:", error);
          });
      }
    },
    [isReading, recordProgress]
  );

  const handleReaderError = useCallback((error) => {
    setBookReaderError(error.message);
  }, []);

  const handleUIToggle = useCallback(() => {
    setUiVisible((prev) => !prev);
  }, []);

  const handleNavigateToPosition = useCallback(
    (characterPosition) => {
      const previousPosition = currentPositionRef.current;

      // Обновляем позицию через FoliateReader
      currentPositionRef.current = characterPosition;

      // Выполняем навигацию в FoliateReader
      if (readerControlsRef.current?.goToPosition) {
        readerControlsRef.current.goToPosition(characterPosition);
      }

      // Немедленно сохраняем прогресс при навигации к закладке
      if (isReading && characterPosition !== previousPosition) {
        console.log("handleNavigateToPosition");

        const charactersRead = Math.max(
          0,
          characterPosition - previousPositionRef.current
        );
        recordProgress(characterPosition, charactersRead)
          .then(() => {
            previousPositionRef.current = characterPosition;
            return;
          })
          .catch((error) => {
            console.error("Failed to record progress on navigation:", error);
          });
      }
    },
    [isReading, recordProgress]
  );

  if (loading) {
    return <LoadingScreen />;
  }

  if (error) {
    return <ErrorScreen error={error} />;
  }

  if (!content) {
    return (
      <ErrorScreen
        title={t("contentNotFound.title", "Контент не найден")}
        text={t("contentNotFound.text", "Запрашиваемый контент не найден")}
      />
    );
  }

  // Обработка ошибок доступа
  if (content.access?.error || bookReaderError) {
    const accessError = content?.access?.error || bookReaderError;
    return (
      <>
        <BookReaderAccessErrors
          content={content}
          accessError={accessError}
          onClickProductButton={dispatch}
        />
        {content.products && (
          <ProductDialog
            products={content.products.nodes}
            state={state}
            dispatch={dispatch}
            refetch={refetch}
          />
        )}
      </>
    );
  }

  // Если нет токена доступа
  if (!content.access?.token) {
    return (
      <>
        <BookReaderAccessErrors
          content={content}
          accessError="NO_ACCESS_TOKEN"
          onClickProductButton={dispatch}
        />
        {content.products && (
          <ProductDialog
            products={content.products.nodes}
            state={state}
            dispatch={dispatch}
            refetch={refetch}
          />
        )}
      </>
    );
  }

  // Ожидание инициализации чтения
  if (!bookData) {
    return <LoadingScreen />;
  }

  // Не рендерим компоненты пока не загружена книга
  if (!bookData?.file?.url || !bookData?.id) {
    return <LoadingScreen />;
  }

  // Основной интерфейс читалки
  return (
    <Box sx={{ height: "100vh", overflow: "hidden", position: "relative" }}>
      <FoliateReader
        bookUrl={bookData.file.url}
        bookMetadata={content?.bookMetadata}
        characterLength={bookData?.characterLength}
        initialFraction={bookData.viewerProgress?.currentFraction}
        initialCfi={
          searchParams.get("cfi") || bookData.viewerProgress?.currentCfi
        }
        onPositionChange={handlePositionChange}
        onError={handleReaderError}
        onUIToggle={handleUIToggle}
      >
        {(readerControls) => {
          // Сохраняем readerControls в ref для использования в handleNavigateToPosition
          readerControlsRef.current = readerControls;

          return (
            <ReaderControls
              bookTitle={content?.title || ""}
              contentId={contentId}
              bookId={bookData.id}
              readerControls={readerControls}
              uiVisible={uiVisible}
              onNavigateToPosition={handleNavigateToPosition}
              content={content}
            />
          );
        }}
      </FoliateReader>
    </Box>
  );
}

BookReader.propTypes = {};
